<template>
  <div
    class="w-full mx-auto px-4 sm:px-8 md:px-10 max-w-screen-xl md:mt-0 mt-8 lg:mt-20"
  >
    <h1
      class="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6 mt-2 sm:mt-12 lg:mt-0 text-los-blue-darkest md:text-center"
    >
      {{ title }}
    </h1>

    <Breadcrumb
      class="mt-4 text-sm text-los-pink-darkest md:text-center"
      :links="breadCrumbLinks"
    />
    <div
      class="md:hidden overflow-hidden w-full border-b-2 border-dashed border-los-blue-light px-2 mt-8"
      :class="{
        'max-h-20': !showIntroContainer,
        'max-h-none': showIntroContainer,
      }"
    >
      <h2 class="heading-basic mb-1 sm:mb-2">Samen gaan we opzoek</h2>
      <button
        class="relative pr-8 cursor-pointer max-w-full text-left"
        @click="showIntroContainer = !showIntroContainer"
      >
        <p
          class="text-basic"
          :class="{
            'overflow-ellipsis whitespace-nowrap overflow-hidden':
              !showIntroContainer,
            'mb-8': showIntroContainer,
          }"
        >
          De beste machine vinden die perfect bij jouw behoeften past, dat is
          ons doel. Bekijk ons aanbod of kom langs in de winkel. We helpen je
          graag met het uitzoeken van de machine die het best bij jou past.
        </p>

        <FontAwesome
          icon="chevron-down"
          class="text-xl absolute right-1 top-0"
          :class="{
            'top-1/2 -translate-y-1/2 rotate-180': showIntroContainer,
          }"
        />
      </button>
    </div>
    <div
      class="hidden md:block overflow-hidden text-center w-full border-b-2 border-dashed border-los-blue-light px-20 pb-10 my-8 sm:mt-12 lg:mb-20"
    >
      <p class="mb-8 text-basic font-normal">
        De beste machine vinden die perfect bij jouw behoeften past, dat is ons
        doel. Bekijk ons aanbod of kom langs in de winkel. We helpen je graag
        met het uitzoeken van de machine die het best bij jou past.
      </p>
    </div>

    <div
      class="grid grid-cols-1 lg:grid-cols-[280px_1fr] lg:gap-x-6 my-4 lg:my-10"
    >
      <div
        class="lg:block"
        :class="{
          'flex justify-end items-start fixed inset-0 z-20 overflow-y-scroll bg-gray-800/70 min-h-screen py-4':
            showFilterOverlay,
          'hidden lg:block': !showFilterOverlay,
        }"
        @click.self="showFilterOverlay = false"
      >
        <div
          class="px-4 pt-2 text-3xl text-white pointer-events-none sticky top-0"
          :class="{
            hidden: !showFilterOverlay,
          }"
        >
          <FontAwesome icon="times" />
        </div>
        <MachinesFilter
          class="w-3/4 h-auto md:h-auto md:w-full"
          :types="machineTypes"
          :type="type"
          :brands="machineBrands"
          :brand="brand"
          :price-low="queryData.price_gte"
          :price-high="queryData.price_lte"
          :sort="queryData.sort"
          :canResetFilters="canResetFilters"
          @onReset="onResetFilters"
          @onChange="onChangeFilters"
        />
      </div>
      <div class="lg:pl-0 py-3 rounded-lg flex flex-col">
        <MachinesSort
          :sort="queryData.sort"
          :canResetFilters="canResetFilters"
          class="mb-6 md:mb-4 lg:pl-4 xl:pl-16"
          @onChange="onChangeFilters"
          @onClickFilter="showFilterOverlay = true"
          @onReset="onResetFilters"
        />

        <transition
          enter-active-class="transition ease-out duration-200 opacity-0 "
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition ease-in duration-250"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ul
            v-if="status === 'success'"
            class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6"
          >
            <template v-if="machinesFound">
              <li v-for="machine in machines" :key="machine.id">
                <MachineItem
                  :machine="machine"
                  :loadImgEager="loadImagesEager"
                />
              </li>
            </template>
            <div
              v-else
              class="col-span-3 row-start-2 flex flex-col justify-center items-center"
            >
              <div
                class="bg-los-yellow-lightest px-6 py-4 rounded-lg max-w-md mt-10"
              >
                <p class="mb-3">
                  Er zijn geen machines gevonden die aan het filter voldoen.
                </p>
                <div class="flex justify-center">
                  <button
                    class="btn-blue flex items-center text-sm"
                    @click="onResetFilters"
                  >
                    Reset alle filters
                    <FontAwesome class="ml-1 text-base" icon="rotate-left" />
                  </button>
                </div>
              </div>
            </div>
          </ul>
        </transition>

        <MachinesPaginationBar class="mt-6" :pagination="pagination" />
      </div>
    </div>
    <div class="my-basic">
      <CtaMachineBrand
        v-if="brand !== 'alle-merken'"
        :brand="brandData"
        :type="typeData"
      />
    </div>
  </div>
</template>

<script setup>
const { params, query, path } = useRoute();
const { find } = useStrapi();

const toastsStore = useToastsStore();

const PAGE_SIZE = 18;
const paramsData = ref(params);
let queryData = ref(query);
const loadImagesEager = ref(true);
const showIntroContainer = ref(false);
const showFilterOverlay = ref(false);

onMounted(() => {
  loadImagesEager.value = false;
});

const { data: machinesPageData } = await useAsyncData('machines-page', () =>
  find('machines-page', { populate: ['seo', 'seo.metaImage'] })
);

const {
  data: {
    value: { data: brands },
  },
} = await useAsyncData('brands', () =>
  find('machine-brands', {
    populate: ['image', 'seo', 'seo.metaImage'],
    sort: ['name'],
  })
);

const {
  data: {
    value: { data: types },
  },
} = await useAsyncData('types', () =>
  find('machine-types', { populate: ['seo', 'seo.metaImage'], sort: ['name'] })
);

const machineTypes = computed(() => [
  { name: 'Alle types', key: 'alle-machines' },
  ...types,
]);

const machineBrands = computed(() => [
  { name: 'Alle merken', key: 'alle-merken' },
  ...brands,
]);

const fetchMachinesHandler = (query, params) =>
  find('machines', {
    populate: ['type', 'brand', 'brand', 'list_image'],
    pagination: {
      pageSize: PAGE_SIZE,
      page: query.page || 1,
    },
    sort: query.sort ?? ['order:asc'],
    filters: {
      price: {
        $gte: query.price_gte ?? undefined,
        $lte: query.price_lte ?? undefined,
      },
      type: {
        key: {
          $eq:
            params.type && params.type !== 'alle-machines'
              ? params.type
              : undefined,
        },
      },
      brand: {
        key: {
          $eq:
            params.brand && params.brand !== 'alle-merken'
              ? params.brand
              : undefined,
        },
      },
    },
  });

const { data: machinesData, status: machinesStatus } = await useAsyncData(
  'machines',
  () => fetchMachinesHandler(query, params)
);

const type = computed(() => paramsData.value.type ?? 'alle-machines');
const brand = computed(() => paramsData.value.brand ?? 'alle-merken');

const brandData = computed(() => {
  return machineBrands.value.find(({ key }) => key === brand.value);
});

const typeData = computed(() => {
  return machineTypes.value.find(({ key }) => key === type.value);
});

const title = computed(() => {
  const t = machineTypes.value.find(({ key }) => key === type.value);
  const b = machineBrands.value.find(({ key }) => key === brand.value);

  let title = t.key === 'alle-machines' ? 'Machines' : t.name;
  title = b && b.key !== 'alle-merken' ? `${b.name} ${title}` : `Alle ${title}`;
  return title;
});

const seo = computed(
  () =>
    brandData.value.seo ??
    typeData.value.seo ??
    machinesPageData.value?.data.seo
);

const breadCrumbLinks = computed(() => {
  let path = useRoute().path.replace(/\/$/, '').split('/');
  if (!path) {
    return [];
  }

  path = path.slice(1, path.length);
  return path.map((slug, index) => {
    return {
      title: slug,
      slug: '/' + path.slice(0, index + 1).join('/'),
    };
  });
});

watch(
  () => title.value,
  (t) => {
    const { origin, pathname } = useRequestURL();

    usePageSeo({
      ...seo.value,
      metaTitle: t + ' - Los Naaimachines',
      canonicalUrl: origin + pathname,
    });
  },
  { immediate: true }
);

const machines = ref(machinesData.value.data);
const status = ref(machinesStatus.value);
const pagination = ref(machinesData.value.meta.pagination);

const machinesFound = computed(() => machines.value?.length);

const canResetFilters = computed(() => {
  return (
    (!!type.value && type.value !== 'alle-machines') ||
    (!!brand.value && brand.value !== 'alle-merken') ||
    !!queryData.value.price_gte ||
    !!queryData.value.price_lte
  );
});

async function fetchMachines() {
  try {
    status.value = 'pending';
    const response = await fetchMachinesHandler(
      queryData.value,
      paramsData.value
    );
    machines.value = response.data;
    pagination.value = response.meta.pagination;
  } catch (error) {
    console.error(error);
    toastsStore.setFailedToast({
      title: 'Oeps! Er is iets fout gegaan.',
      text: 'We hebben de machines niet op kunnen halen. Probeer het later nog eens.',
    });
  } finally {
    status.value = 'success';
  }
}

async function onChangeFilters({ key, value }) {
  const newQuery = { ...queryData.value, page: undefined };

  let path = '/machines';
  let typeSlug = paramsData.value.type ?? '';
  let brandSlug = paramsData.value.brand ?? '';

  if (key === 'type' || key === 'brand') {
    if (key === 'type') {
      typeSlug = value;
    } else if (key === 'brand') {
      brandSlug = value;
    }
    paramsData.value[key] = value;
  } else {
    if (!value || value === undefined) {
      delete newQuery[key];
    } else {
      newQuery[key] = value;
    }
  }
  queryData.value = newQuery;

  if (typeSlug) {
    path = `${path}/${typeSlug}/${brandSlug ? brandSlug : ''}`;
  } else if (brandSlug) {
    path = `${path}/alle-machines/${brandSlug}`;
  }

  const err = await navigateTo({
    path,
    query: { ...queryData.value },
  });

  if (err) {
    await navigateTo({
      path: '/machines',
    });
    await navigateTo({
      path,
      query: { ...queryData.value },
    });
  }

  fetchMachines();
}

async function onResetFilters() {
  const sort = queryData.value.sort ?? null;
  queryData.value = sort ? { sort } : {};
  paramsData.value = {};
  await navigateTo({
    path: '/machines',
    query: queryData.value,
  });
  fetchMachines();
}

useHead({
  bodyAttrs: {
    class: computed(() => {
      if (showFilterOverlay.value) return 'filter-overlay-is-open';
      return '';
    }),
  },
});
</script>

<style lang="scss" scoped></style>
